import { AlertTriangle } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import { shortSideLabel, type QEPhoto } from "./types"

// Quick Estimate 3 — photo tile.
//
// Card-shaped: image on top, footer strip on the bottom. The whole
// card is one tap-target — image and footer both open the editor —
// so we don't rely on icon-on-image overlays or "tap to inspect"
// hint copy. The footer reads as an obvious clickable row (label +
// edit affordance), which carries the discoverability the previous
// design lacked.
//
// State drives footer color + label:
//   • good      → emerald dot, side name, edit pencil-dot
//   • lowQ      → amber dot + AlertTriangle on image, edit pencil-dot
//   • failed    → rose footer, "Couldn't tag", chevron-style affordance
//   • untagged  → zinc dot, "Untagged", edit pencil-dot

interface PhotoTileProps {
  photo: QEPhoto
  onOpen: () => void
}

export function PhotoTile({ photo, onOpen }: PhotoTileProps) {
  const failed = photo.verdict === "eval-failed"
  const lowQuality =
    !failed &&
    photo.uploadedUrl.length > 0 &&
    !photo.qualified &&
    !photo.sendWithoutAiTag

  // Dot color encodes quality at a glance.
  const dotColor = failed
    ? "bg-rose-500"
    : photo.sendWithoutAiTag
      ? "bg-zinc-400"
      : photo.qualified
        ? "bg-emerald-500"
        : "bg-amber-500"

  const labelText = photo.sendWithoutAiTag
    ? "Untagged"
    : failed
      ? "Couldn't tag"
      : shortSideLabel(photo.detectedSide)

  return (
    <div
      className={cx(
        "rounded-2xl overflow-hidden border bg-white shadow-sm",
        "flex flex-col",
        failed
          ? "border-rose-200/80"
          : lowQuality
            ? "border-amber-200/80"
            : "border-zinc-200/80",
      )}
    >
      {/* Image — tap to open editor. */}
      <button
        type="button"
        onClick={onOpen}
        aria-label="Edit photo"
        className="relative aspect-square w-full bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
      >
        <img
          src={photo.previewUrl}
          alt={shortSideLabel(photo.detectedSide)}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {(failed || lowQuality) && (
          <div
            className={cx(
              "absolute top-2 left-2 size-6 rounded-full grid place-items-center shadow ring-1 ring-white/40",
              failed ? "bg-rose-500/95" : "bg-amber-500/95",
            )}
            aria-hidden
          >
            <AlertTriangle
              className="size-3 text-white"
              strokeWidth={2.5}
              aria-hidden
            />
          </div>
        )}
        {photo.sideOverridden && !failed && (
          <span className="absolute top-2 right-2 px-1.5 h-5 inline-flex items-center rounded-full bg-indigo-500/90 text-white text-[9px] font-bold uppercase tracking-wider ring-1 ring-white/30 shadow">
            Edited
          </span>
        )}
      </button>

      {/* Footer — also tappable. The "edit affordance" on the right
          makes the row read as a clickable list item rather than a
          decorative caption. */}
      <button
        type="button"
        onClick={onOpen}
        aria-label="Edit photo"
        className={cx(
          "flex items-center justify-between gap-2 px-2.5 py-2 border-t",
          "transition-colors duration-150",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500",
          failed
            ? "bg-rose-50 hover:bg-rose-100/70 border-rose-100"
            : "bg-white hover:bg-zinc-50 border-zinc-100",
        )}
      >
        <span className="flex items-center gap-1.5 min-w-0 flex-1">
          <span
            className={cx("size-1.5 rounded-full shrink-0", dotColor)}
            aria-hidden
          />
          <span
            className={cx(
              "text-[12px] font-semibold leading-none truncate",
              failed ? "text-rose-700" : "text-zinc-800",
            )}
          >
            {labelText}
          </span>
        </span>
        <span
          className={cx(
            "size-5 rounded-full grid place-items-center shrink-0",
            failed
              ? "bg-rose-500 text-white"
              : "bg-zinc-100 text-zinc-500 group-hover:bg-zinc-200",
          )}
          aria-hidden
        >
          <PencilDot />
        </span>
      </button>
    </div>
  )
}

// Tiny pencil-dot icon for the footer affordance. Inline SVG so it
// renders the same size as the dot regardless of icon-pack stroke.
function PencilDot() {
  return (
    <svg viewBox="0 0 12 12" fill="none" className="size-3" aria-hidden>
      <path
        d="M2.5 9.5l1-3.5L7 2.5l2.5 2.5L6 8.5l-3.5 1z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
